/**
 * Copyright (c) 2019 Mozilla Inc. All Rights Reserved.
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 */
import * as mat4 from "gl-matrix/src/gl-matrix/mat4";
import * as vec3 from "gl-matrix/src/gl-matrix/vec3";

import XRDevice from "webxr-polyfill/src/devices/XRDevice";
import { PRIVATE as XRSESSION_PRIVATE } from "webxr-polyfill/src/api/XRSession";
import XRSessionEvent from "webxr-polyfill/src/api/XRSessionEvent";

import { throttle } from "../lib/throttle.js";

import ARKitWrapper from "./ARKitWrapper.js";
import ARKitWatcher from "./ARKitWatcher.js";
import GamepadXRInputSource from "../extensions/GamepadXRInputSource.js";

import {
  getTrackedImageScores,
  getImageTrackingResults,
} from "./ImageTracking.js";

export default class ARKitDevice extends XRDevice {
  constructor(global) {
    super(global);
    this._throttledLogPose = throttle(this.logPose, 1000);

    this._sessions = new Map();
    this._activeSession = null; // active ARKit session
    this._frameSession = null; // session for the current frame
    this._hackySessionLock = false;

    // Image Tracking
    this._trackedImages = [];
    this.getTrackedImageScores = getTrackedImageScores.bind(this);
    this.getImageTrackingResults = getImageTrackingResults.bind(this);

    // A div prepended to body children that will contain the session layer
    this._wrapperDiv = document.createElement("div");
    this._wrapperDiv.setAttribute("class", "arkit-device-wrapper");

    this._floorOffsetMatrix = mat4.create();

    const insertWrapperDiv = () => {
      document.body.insertBefore(
        this._wrapperDiv,
        document.body.firstChild || null
      );
    };

    if (document.body) {
      insertWrapperDiv();
    } else {
      document.addEventListener("DOMContentLoaded", insertWrapperDiv);
    }

    this._headModelMatrix = mat4.create();
    this._headModelMatrixInverse = mat4.create();

    this._projectionMatrix = mat4.create();
    this._deviceProjectionMatrix = mat4.create();
    this._eyeLevelMatrix = mat4.identity(mat4.create());
    this._stageMatrix = mat4.identity(mat4.create());
    this._stageMatrix[13] = 1.3; // HAX - magic value to match ARKit's stage height
    this._identityMatrix = mat4.identity(mat4.create());
    this._baseFrameSet = false;
    this._frameOfRefRequestsWaiting = [];

    this._depthNear = 0.1;
    this._depthFar = 1000;

    try {
      this._arKitWrapper = ARKitWrapper.GetOrCreate();
      this._arWatcher = new ARWatcher(this._arKitWrapper, this);
    } catch (e) {
      console.error("Error initializing the ARKit wrapper", e);
      this._arKitWrapper = null;
      this._arWatcher = null;
    }

    // hit test

    this._hitTestSources = [];
    this._hitTestResults = new Map();
    this._hitTestResultsForNextFrame = new Map();

    this._transientHitTestSources = [];
    this._transientHitTestResults = new Map();
    this._transientHitTestResultsForNextFrame = new Map();

    // DOM overlay

    this._domOverlayRoot = null;
    this._topMostDomElement = null;

    // XRSessionEvent named beforexrselect needs active XRSession instance.
    // @TODO: Ideally the event should be dispatched from XRSession in webxr-polyfill.js
    // and ARKitDevice shouldn't have reference to XRSession?
    this._activeXRSession = null;

    // Input set up
    // Note: This touch input event is not AR kit specifi so that
    // should we move this logic out of ARKitDevice?

    this._gamepads = [];
    this._gamepadInputSources = [];
    this._touches = [];

    const primaryButtonIndex = 0;
    this._gamepads.push(createGamepad("", "none", 1, false));
    this._gamepadInputSources.push(new GamepadXRInputSource(this, {}, 0));
    // Add active property to handle input as trasient input.
    // Set true only when the screen is touched.
    this._gamepadInputSources[0]._active = false;
    this._gamepadInputSources[0].gamepad = this._gamepads[0];

    this._touches.push({ x: 0, y: 0 });

    const getTopMostDomElement = (x, y) => {
      if (!this._domOverlayRoot) {
        return null;
      }
      const elements = document.elementsFromPoint(x, y);
      for (const element of elements) {
        if (this._domOverlayRoot.contains(element)) {
          return element;
        }
      }
      return null;
    };

    document.addEventListener("touchstart", (event) => {
      if (!event.touches || event.touches.length == 0) {
        return;
      }

      const touch = event.touches[0];
      const x = touch.clientX;
      const y = touch.clientY;

      this._topMostDomElement = getTopMostDomElement(x, y);

      this._touches[0].x = x;
      this._touches[0].y = y;

      // normalize the touch position to [-1, 1] and assign to gamepad axes
      const gamepad = this._gamepads[0];
      gamepad.axes[0] = (x / window.innerWidth) * 2 - 1;
      gamepad.axes[1] = (y / window.innerHeight) * 2 - 1;

      const button = this._gamepads[0].buttons[primaryButtonIndex];
      button.pressed = true;
      button.value = 1.0;
    });

    document.addEventListener("touchmove", (event) => {
      if (!event.touches || event.touches.length == 0) {
        return;
      }

      const touch = event.touches[0];
      const x = touch.clientX;
      const y = touch.clientY;

      // normalize the touch position to [-1, 1] and assign to gamepad axes
      const gamepad = this._gamepads[0];
      gamepad.axes[0] = (x / window.innerWidth) * 2 - 1;
      gamepad.axes[1] = (y / window.innerHeight) * 2 - 1;

      this._topMostDomElement = getTopMostDomElement(x, y);

      this._touches[0].x = x;
      this._touches[0].y = y;
    });

    document.addEventListener("touchend", (event) => {
      const x = this._touches[0].x;
      const y = this._touches[0].y;

      this._topMostDomElement = getTopMostDomElement(x, y);

      const button = this._gamepads[0].buttons[primaryButtonIndex];
      button.pressed = false;
      button.value = 0.0;
      //this._touches[0].x = -1;
      //this._touches[0].y = -1;

      const gamepad = this._gamepads[0];
      //gamepad.axes[0] = 0;
      //gamepad.axes[1] = 0;
    });
  }

  // ARKitDevice specific methods

  static initStyles() {
    const init = () => {
      const styleEl = document.createElement("style");
      document.head.appendChild(styleEl);
      const styleSheet = styleEl.sheet;
      styleSheet.insertRule(
        ".arkit-device-wrapper { z-index: -1; display: none; }",
        0
      );
      styleSheet.insertRule(
        ".arkit-device-wrapper, .xr-canvas { background-color: transparent; position: absolute; top: 0; left: 0; bottom: 0; right: 0; }",
        0
      );
      styleSheet.insertRule(
        ".arkit-device-wrapper, .arkit-device-wrapper canvas { width: 100%; height: 100%; padding: 0; margin: 0; -webkit-user-select: none; user-select: none; }",
        0
      );
    };

    if (document.body) {
      init();
    } else {
      window.addEventListener("DOMContentLoaded", init);
    }
  }

  logPose() {
    window.VLaunch.log(
      "pose",
      mat4.getTranslation(new Float32Array(3), this._headModelMatrixInverse),
      mat4.getRotation(new Float32Array(4), this._headModelMatrixInverse)
    );
  }

  // HitTest

  addHitTestSource(source) {
    if (!this._hitTestSources.includes(source)) {
      this._hitTestSources.push(source);
    }
  }

  addTransientHitTestSource(source) {
    if (!this._transientHitTestSources.includes(source)) {
      this._transientHitTestSources.push(source);
    }
  }

  _runHitTest() {
    // - Clear _hitTestResults
    // - Remove inactive hit test sources
    // - Copy the results from _hitTestResultsForNextFrame to _hitTestResults
    //   for active hit test source
    // - Clear _hitTestResultsForNextFrame

    this._hitTestResults.clear();
    let sourceNum = 0;
    for (let i = 0; i < this._hitTestSources.length; i++) {
      const source = this._hitTestSources[i];
      if (source._active) {
        if (source._session[XRSESSION_PRIVATE].ended) {
          source.cancel();
        } else {
          this._hitTestSources[sourceNum++] = source;
          if (this._hitTestResultsForNextFrame.has(source)) {
            this._hitTestResults.set(
              source,
              this._hitTestResultsForNextFrame.get(source)
            );
          }
        }
      }
    }
    this._hitTestSources.length = sourceNum;
    this._hitTestResultsForNextFrame.clear();

    if (!this._arKitWrapper) {
      console.error("Hit test requires ARKitWrapper.");
      return;
    }

    // Run hit test against all the registered active hit test sources

    for (const source of this._hitTestSources) {
      // Convert from ray direction to screen coordinates 0.0 - 1.0 for iOS HitTest API.
      const proj = vec3.create();
      proj[0] = source._offsetRay.direction.x;
      proj[1] = source._offsetRay.direction.y;
      proj[2] = source._offsetRay.direction.z;
      vec3.transformMat4(proj, proj, this._arKitWrapper._projectionMatrix);
      const x = (proj[0] + 1.0) * 0.5;
      const y = (-proj[1] + 1.0) * 0.5;

      // Perform the hit test.
      // Our hit test is async method so storing the hit test results for next release.
      // Then hit test will be one frame behind but we think it's acceptable so far.
      // 0..1 (0,0 is at top left and 1,1 is at bottom right)
      this._arKitWrapper
        .hitTest(x, y, ARKitWrapper.HIT_TEST_TYPE_EXISTING_PLANE_USING_GEOMETRY)
        .then((hits) => {
          if (!this._hitTestResultsForNextFrame.has(source)) {
            this._hitTestResultsForNextFrame.set(source, []);
          }
          const results = this._hitTestResultsForNextFrame.get(source);
          for (const hit of hits) {
            results.push(hit);
          }
        });
    }
  }

  // TODO - merge back with normal hit test once I'm more familiar with the differences

  _runTransientHitTest() {
    // - Clear _hitTestResults
    // - Remove inactive hit test sources
    // - Copy the results from _hitTestResultsForNextFrame to _hitTestResults
    //   for active hit test source
    // - Clear _hitTestResultsForNextFrame

    this._transientHitTestResults.clear();
    let sourceNum = 0;
    for (let i = 0; i < this._transientHitTestSources.length; i++) {
      const source = this._transientHitTestSources[i];
      if (source._active) {
        if (source._session[XRSESSION_PRIVATE].ended) {
          source.cancel();
        } else {
          this._transientHitTestSources[sourceNum++] = source;
          if (this._transientHitTestResultsForNextFrame.has(source)) {
            this._transientHitTestResults.set(
              source,
              this._transientHitTestResultsForNextFrame.get(source)
            );
          }
        }
      }
    }
    this._transientHitTestSources.length = sourceNum;
    this._transientHitTestResultsForNextFrame.clear();

    if (!this._arKitWrapper) {
      console.error("Transient hit test requires ARKitWrapper.");
      return;
    }

    // Run hit test against all the registered active hit test sources

    for (const source of this._transientHitTestSources) {
      const transientInputSource = this._gamepadInputSources[0];
      if (!transientInputSource._active) {
        continue;
      }

      // Launch only supports generic touchscreen transient input, so use the
      // active touch location as the hit-test ray.
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const viewportHeight =
        document.documentElement.clientHeight || window.innerHeight;
      const x = this._touches[0].x / viewportWidth;
      const y = this._touches[0].y / viewportHeight;

      // Perform the hit test.
      // Our hit test is async method so storing the hit test results for next release.
      // Then hit test will be one frame behind but we think it's acceptable so far.
      // 0..1 (0,0 is at top left and 1,1 is at bottom right)
      this._arKitWrapper
        .hitTest(x, y, ARKitWrapper.HIT_TEST_TYPE_EXISTING_PLANE_USING_GEOMETRY)
        .then((hits) => {
          if (!this._transientHitTestResultsForNextFrame.has(source)) {
            this._transientHitTestResultsForNextFrame.set(source, []);
          }
          const results = this._transientHitTestResultsForNextFrame.get(source);

          for (const hit of hits) {
            results.push(hit);
          }
        });
    }
  }

  getHitTestResults(source) {
    return this._hitTestResults.has(source)
      ? this._hitTestResults.get(source)
      : [];
  }

  getTransientHitTestResults(transientHitTestSource) {
    if (!this._transientHitTestResults.has(transientHitTestSource)) return [];
    const hits = this._transientHitTestResults.get(transientHitTestSource);
    return hits;
  }

  // DOM overlay

  setDomOverlayRoot(root) {
    this._domOverlayRoot = root;
  }

  setActiveXRSession(xrSession) {
    this._activeXRSession = xrSession;
  }

  _shouldSuppressSelectEvents() {
    if (this._topMostDomElement && this._topMostDomElement.dispatchEvent) {
      const event = new XRSessionEvent("beforexrselect", {
        session: this._activeXRSession,
        cancelable: true,
      });
      // Assuming event handlers are called synchronously. Is it correct?
      this._topMostDomElement.dispatchEvent(event);
      if (event.defaultPrevented) {
        return true;
      }
    }
    return false;
  }

  // set methods called by ARWatcher when the device data is sent from Apple ARKit

  setProjectionMatrix(matrix) {
    mat4.copy(this._deviceProjectionMatrix, matrix);
  }

  setBaseViewMatrix(matrix) {
    // ARKit provides the camera transform. Store both the pose matrix and its
    // inverse view matrix because the polyfill consumes both each frame.
    mat4.copy(this._headModelMatrixInverse, matrix);
    mat4.invert(this._headModelMatrix, this._headModelMatrixInverse);

    if (!this._baseFrameSet) {
      this._baseFrameSet = true;
      for (let i = 0; i < this._frameOfRefRequestsWaiting.length; i++) {
        const callback = this._frameOfRefRequestsWaiting[i];
        try {
          callback();
        } catch (e) {
          console.error("finalization of reference frame requests failed: ", e);
        }
      }
      this._frameOfRefRequestsWaiting.length = 0;
    }
  }

  // XRDevice methods

  get depthNear() {
    return this._depthNear;
  }
  set depthNear(val) {
    this._depthNear = val;
  }

  get depthFar() {
    return this._depthFar;
  }
  set depthFar(val) {
    this._depthFar = val;
  }

  isSessionSupported(mode) {
    // Note: We support only immersive-ar mode for now.
    //       See https://github.com/MozillaReality/webxr-ios-js/pull/34#discussion_r334910337
    return mode === "immersive-ar" || mode === "inline";
  }

  /**
   * @param {string} featureDescriptor
   * @return {boolean}
   */
  isFeatureSupported(featureDescriptor) {
    switch (featureDescriptor) {
      //official + supported
      case "viewer":
        return true;
      case "local":
        return true;
      case "image-tracking":
        return true;
      case "hit-test":
        return true;
      case "dom-overlay":
        return true;
      case "anchors":
        return true;

      // @TODO: need to support local-floor, bounded and unbounded. local-floor is semi-supported currently
      case "local-floor":
        return true;
      //unsupported
      case "bounded":
        return false;
      case "unbounded":
        return false;
      case "computer-vision-launch":
        return true;
      case "computer-vision":
        return false;

      case "alignEUS":
        return true;

      default:
        return false;
    }
  }

  /**
   * @param {number} sessionId
   * @param {XRReferenceSpaceType} type
   * @return {boolean}
   */
  doesSessionSupportReferenceSpace(sessionId, type) {
    const session = this._sessions.get(sessionId);
    if (session.ended) {
      return false;
    }

    if (!session.enabledFeatures.has(type)) {
      // if the feature isn't supported on this session, return false
      return false;
    }

    // now check it's a valid reference space
    switch (type) {
      case "viewer":
        return true;
      case "local":
        return true;

      // @TODO: need to support local-floor, bounded and unbounded
      case "local-floor":
        return true;
      case "bounded":
        return false;
      case "unbounded":
        return false;

      default:
        return false;
    }
  }

  async setDetectionImages(trackedImages) {
    if (this._arKitWrapper) {
      //trackedImages each must have widthInMeters & width/height must be > 100 pixels
      trackedImages.forEach((image) => {
        if (image.widthInMeters === undefined || image.widthInMeters === null) {
          throw new Error("trackedImages must have widthInMeters set", image);
        }
        if (image.width < 100 || image.height < 100) {
          throw new Error(
            "trackedImages must have width/height > 100 pixels",
            image
          );
        }
      });

      this._trackedImages = await this._arKitWrapper._createDetectionImages(
        trackedImages
      ); //promise
      window.VLaunch.log("setDetectionImages", this._trackedImages);
      return this._trackedImages;
    }
  }

  async requestSession(mode, enabledFeatures) {
    if (!this.isSessionSupported(mode)) {
      console.error("Invalid session mode", mode);
      return Promise.reject();
    }

    if (mode === "inline") {
      const session = new Session(mode, enabledFeatures);

      this._sessions.set(session.id, session);

      return Promise.resolve(session.id);
    }

    if (!this._arKitWrapper) {
      console.error("Session requested without an ARKitWrapper");
      return Promise.reject();
    }
    if (this._activeSession !== null) {
      console.error("Tried to start a second session while first is running.");
      return Promise.reject();
    }

    const ARKitOptions = {};
    if (enabledFeatures.has("worldSensing")) {
      ARKitOptions.worldSensing = true;
    }
    //added for launch to get close to official WebXR spec
    if (enabledFeatures.has("camera-access-launch")) {
      ARKitOptions.videoFrames = true;
      ARKitOptions.worldSensing = true;
    }

    if (enabledFeatures.has("computerVision")) {
      ARKitOptions.videoFrames = true;
    }
    if (enabledFeatures.has("alignEUS")) {
      ARKitOptions.alignEUS = true;
    }

    if (enabledFeatures.has("image-tracking")) {
      ARKitOptions.imageTracking = true;
    }

    await this._arKitWrapper
      .waitForInit()
      .then(() => {})
      .catch((...params) => {
        console.error("app failed to initialize: ", ...params);
        return Promise.reject();
      });

    const watchResult = await this._arKitWrapper
      .watch(ARKitOptions)
      .then(async (results) => {
        const session = new Session(mode, enabledFeatures);

        this._sessions.set(session.id, session);
        this._activeSession = session;
        window.VLaunch.log("session created: ", this._activeSession.id);

        this.dispatchEvent("@@webxr-polyfill/vr-present-start", session.id);

        if (ARKitOptions.imageTracking) {
          window.VLaunch.log("pre _createDetectionImages", this._trackedImages);
          this._trackedImages = await this._arKitWrapper._createDetectionImages(
            this._trackedImages
          );
          window.VLaunch.log(
            "post _createDetectionImages",
            this._trackedImages
          );
        }

        return Promise.resolve(session.id);
      })
      .catch((...params) => {
        console.error("session request failed: ", ...params);
        return Promise.reject();
      });

    return watchResult;
  }

  onBaseLayerSet(sessionId, layer) {
    //HAX - this currently gets called twice. cant find why but need to prevent changes
    if (this._hackySessionLock == true) {
      return;
    }
    const session = this._sessions.get(sessionId);
    const canvas = layer.context.canvas;
    const oldLayer = session.baseLayer;

    session.baseLayer = layer; // XRWebGLLayer

    if (!session.immersive) {
      return;
    }

    if (oldLayer !== null) {
      const oldCanvas = oldLayer.context.canvas;
      this._wrapperDiv.removeChild(oldCanvas);

      if (oldCanvas.style) {
        //offscreen canvas will not have style
        oldCanvas.style.width = session.canvasWidth;
        oldCanvas.style.height = session.canvasHeight;
        oldCanvas.style.display = session.canvasDisplay;
        oldCanvas.style.backgroundColor = session.canvasBackground;
      }
    }

    if (document.body) {
      //document body can be null in iframes
      session.bodyBackgroundColor = document.body.style.backgroundColor;
      session.bodyBackgroundImage = document.body.style.backgroundImage;
      document.body.style.backgroundColor = "transparent";
      document.body.style.backgroundImage = "none";
    }

    var children = document.body.children;

    this._hackySessionLock = true;

    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      var isChildOfDomOverlay = false;
      if (this._domOverlayRoot && this._domOverlayRoot.contains(child)) {
        isChildOfDomOverlay = true;
      }
      //console log all values in the if statement
      if (
        child != this._wrapperDiv &&
        child != canvas &&
        child.id !== "eruda" && //don't remove the custom console logger
        !isChildOfDomOverlay
      ) {
        var display = child.style.display;
        child._displayChanged = true;
        child._displayWas = display;
        child.style.display = "none";
      }
    }

    session.canvasParent = canvas.parentNode;
    session.canvasNextSibling = canvas.nextSibling;
    if (canvas.style) {
      session.canvasDisplay = canvas.style.display;
      canvas.style.display = "block";
      session.canvasBackground = canvas.style.backgroundColor;
      canvas.style.backgroundColor = "transparent";
      session.canvasWidth = canvas.style.width;
      session.canvasHeight = canvas.style.height;
      canvas.style.width = "100%";
      canvas.style.height = "100%";
      this._wrapperDiv.appendChild(canvas);
      this._wrapperDiv.style.display = "block";
    }

    // layer.width = layer.context.canvas.width = this._wrapperDiv.clientWidth * window.devicePixelRatio;
    // layer.height = layer.context.canvas.height = this._wrapperDiv.clientHeight * window.devicePixelRatio;
  }

  userEndedSession() {
    if (this._activeSession) {
      let session = this._activeSession;
      if (session.immersive && !session.ended) {
        this.endSession(session.id);

        this.dispatchEvent("@@webxr-polyfill/vr-present-end", session.id);
      }
    }
  }

  endSession(sessionId) {
    const session = this._sessions.get(sessionId);
    if (!session || session.ended) {
      return;
    }

    session.ended = true;

    // if (this._activeSession === session) {
    if (session.baseLayer !== null) {
      // TODO - can't recall what this check is for, do we need it?
      var children = document.body.children;
      for (var i = 0; i < children.length; i++) {
        var child = children[i];
        if (child != this._wrapperDiv) {
          if (child._displayChanged == true) {
            if (child._displayWas) {
              child.style.display = child._displayWas;
            } else {
              child.style.removeProperty("display");
            }
            child._displayWas = "";
            child._displayChanged = false;
          }
        }
      }

      const canvas = session.baseLayer.context.canvas;
      this._wrapperDiv.removeChild(canvas);

      if (!session.canvasNextSibling) {
        if (session.canvasParent) {
          // was at the end
          session.canvasParent.appendChild(canvas);
        } else {
          // it wasn't in the hierarchy at all
        }
      } else {
        session.canvasNextSibling.before(canvas);
      }
      session.canvasParent = null;
      session.canvasNextSibling = null;

      if (canvas.style) {
        canvas.style.width = session.canvasWidth;
        canvas.style.height = session.canvasHeight;
        canvas.style.display = session.canvasDisplay;
        canvas.style.backgroundColor = session.canvasBackground;
      }
      if (document.body) {
        document.body.style.backgroundColor = session.bodyBackgroundColor;
        document.body.style.backgroundImage = session.bodyBackgroundImage;
      }
    }

    this._wrapperDiv.style.display = "none";

    this._activeSession = null;
    mat4.identity(this._headModelMatrix);
    mat4.identity(this._headModelMatrixInverse);
    this._arKitWrapper.stop();

    this._domOverlayRoot = null;
    this._topMostDomElement = null;
    this._activeXRSession = null;
    this._hackySessionLock = false;
    //}
    this._frameSession = null;
  }

  requestAnimationFrame(callback, ...params) {
    // if (this._activeSession) {
    return this._arKitWrapper.requestAnimationFrame(callback, params);
    // } else {
    // 	return window.requestAnimationFrame(callback, params);
    // }
  }

  cancelAnimationFrame(handle) {
    return this._arKitWrapper.cancelAnimationFrame(handle);
  }

  onFrameStart(sessionId, renderState) {
    const session = this._sessions.get(sessionId);

    this._frameSession = session;

    // If the session is inline make sure the projection matrix matches the
    // aspect ratio of the underlying WebGL canvas.
    if (session.immersive) {
      mat4.copy(this._projectionMatrix, this._deviceProjectionMatrix);
      // If session is not an inline session, XRWebGLLayer's composition disabled boolean
      // should be false and then framebuffer should be marked as opaque.
      // The buffers attached to an opaque framebuffer must be cleared prior to the
      // processing of each XR animation frame.
      if (session.baseLayer) {
        const context = session.baseLayer.context;
        const currentClearColor = context.getParameter(
          context.COLOR_CLEAR_VALUE
        );
        const currentClearDepth = context.getParameter(
          context.DEPTH_CLEAR_VALUE
        );
        const currentClearStencil = context.getParameter(
          context.STENCIL_CLEAR_VALUE
        );
        context.clearColor(0.0, 0.0, 0.0, 0.0);
        context.clearDepth(1, 0);
        context.clearStencil(0.0);
        context.clear(
          context.DEPTH_BUFFER_BIT |
            context.COLOR_BUFFER_BIT |
            context.STENCIL_BUFFER_BIT
        );
        context.clearColor(
          currentClearColor[0],
          currentClearColor[1],
          currentClearColor[2],
          currentClearColor[3]
        );
        context.clearDepth(currentClearDepth);
        context.clearStencil(currentClearStencil);
      }
    } else {
      if (session.baseLayer) {
        const canvas = session.baseLayer.context.canvas;
        // Update the projection matrix.
        mat4.perspective(
          this._projectionMatrix,
          renderState.inlineVerticalFieldOfView,
          canvas.width / canvas.height,
          renderState.depthNear,
          renderState.depthFar
        );
      }
    }

    // input event

    if (session.immersive) {
      for (let i = 0; i < this._gamepads.length; ++i) {
        const gamepad = this._gamepads[i];
        const inputSourceImpl = this._gamepadInputSources[i];
        inputSourceImpl.updateFromGamepad(gamepad);
        // @TODO: temporal workaround because the polyfill doesn't have a way to set 'screen'.
        //        We should send the feedback to the polyfill.
        inputSourceImpl.targetRayMode = "screen";
        if (inputSourceImpl.primaryButtonIndex !== -1) {
          const primaryActionPressed =
            gamepad.buttons[inputSourceImpl.primaryButtonIndex].pressed;
          if (primaryActionPressed && !inputSourceImpl.primaryActionPressed) {
            this._gamepadInputSources[0]._active = true;
            // select start event is fired in onFrameEnd().
            // See the comment there for the detail.
          } else if (
            !primaryActionPressed &&
            inputSourceImpl.primaryActionPressed
          ) {
            if (!this._shouldSuppressSelectEvents()) {
              this.dispatchEvent("@@webxr-polyfill/input-select-end", {
                sessionId: session.id,
                inputSource: inputSourceImpl.inputSource,
              });
            }
            this._gamepadInputSources[0]._active = false;
          }
          // New inputSourceImpl.primaryActionPressed is saved in onFrameEnd()
        }
      }
    }

    this._runHitTest();
    this._runTransientHitTest();
  }

  onFrameEnd(sessionId) {
    const session = this._sessions.get(sessionId);
    // We handle input event as transient input event.
    // If primary action happens on transient input
    // 1. First fire intputsourceschange event
    // 2. And then fire select start event
    // But in webxr-polyfill.js, inputsourceschange event is fired
    // after onFrameStart() by making an input source active in onFrameStart().
    // So we need to postpone input select start event until onFrameEnd() here.
    // Regarding select and select end events, they should be fired
    // before inputsourceschange event, so ok to be in onFrameStart().
    if (session.immersive) {
      for (let i = 0; i < this._gamepads.length; ++i) {
        const gamepad = this._gamepads[i];
        const inputSourceImpl = this._gamepadInputSources[i];
        if (inputSourceImpl.primaryButtonIndex !== -1) {
          const primaryActionPressed =
            gamepad.buttons[inputSourceImpl.primaryButtonIndex].pressed;
          if (
            primaryActionPressed &&
            !inputSourceImpl.primaryActionPressed &&
            !this._shouldSuppressSelectEvents()
          ) {
            this.dispatchEvent("@@webxr-polyfill/input-select-start", {
              sessionId: session.id,
              inputSource: inputSourceImpl.inputSource,
            });
          }
          inputSourceImpl.primaryActionPressed = primaryActionPressed;
        }
      }
    }

    this._frameSession = null;
  }

  requestFrameOfReferenceTransform(type, options) {
    return new Promise((resolve, reject) => {
      const enqueueOrExec = (callback) => {
        if (this._baseFrameSet) {
          callback();
        } else {
          this._frameOfRefRequestsWaiting.push(callback);
        }
      };

      switch (type) {
        case "viewer":
          enqueueOrExec(() => {
            resolve(this._headModelMatrixInverse);
          });
          return;
        case "local":
          enqueueOrExec(() => {
            resolve(this._eyeLevelMatrix);
          });
          return;
        case "local-floor":
          enqueueOrExec(() => {
            resolve(this._stageMatrix);
          });
          return;

        case "bounded-floor":

        // @TODO: Support unbounded reference space.
        // @TODO: Support reset event.
        //        In ARKit, the native origin can change as the user moves around.
        //        In unbounded, the space origin can change. But in other space
        //        such as local and local-floor, if the origin changes, a reset event should
        //        be triggered on the coordinate system (per the spec).
        case "unbounded":
          reject(new Error("not supported " + type));
          return;
        default:
          reject(new Error("Unsupported frame of reference type " + type));
          return;
      }
    });
  }

  getViewport(sessionId, eye, layer, target) {
    // A single viewport that covers the entire screen
    const { width, height } = layer.context.canvas;
    target.x = 0;
    target.y = 0;
    target.width = width;
    target.height = height;
    return true;
  }

  getProjectionMatrix(eye) {
    return this._projectionMatrix;
  }

  getBasePoseMatrix() {
    if (this._frameSession.immersive) {
      return this._headModelMatrixInverse;
    } else {
      return this._identityMatrix;
    }
  }

  getBaseViewMatrix(eye) {
    if (this._frameSession.immersive) {
      return this._headModelMatrix;
    } else {
      return this._identityMatrix;
    }
  }

  requestStageBounds() {
    return null;
  }

  getInputSources() {
    const inputSources = [];
    for (const inputSourceImpl of this._gamepadInputSources) {
      if (inputSourceImpl._active) {
        inputSources.push(inputSourceImpl.inputSource);
      }
    }
    return inputSources;
  }

  getInputPose(inputSource, coordinateSystem, poseType) {
    for (let i = 0; i < this._gamepadInputSources.length; i++) {
      const inputSourceImpl = this._gamepadInputSources[i];
      if (
        inputSourceImpl._active &&
        inputSourceImpl.inputSource === inputSource
      ) {
        const fov = 2.0 * Math.atan(1.0 / this._projectionMatrix[5]);
        const halfFov = fov * 0.5;
        const near =
          this._projectionMatrix[14] / (this._projectionMatrix[10] - 1.0);
        const aspect = this._projectionMatrix[5] / this._projectionMatrix[0];

        const deviceWidth = document.documentElement.clientWidth;
        const deviceHeight = document.documentElement.clientHeight;
        const clientX = this._touches[i].x;
        const clientY = this._touches[i].y;

        // convert to -1.0 to 1.0
        const normalizedX = (clientX / deviceWidth) * 2.0 - 1.0;
        const normalizedY = -(clientY / deviceHeight) * 2.0 + 1.0;

        // @TODO: Add note about this matrix calculation
        // @TODO: Optimize if possible
        const viewMatrixInverse = mat4.invert(
          mat4.create(),
          this._headModelMatrix
        );
        coordinateSystem._transformBasePoseMatrix(
          viewMatrixInverse,
          viewMatrixInverse
        );

        const matrix = mat4.identity(mat4.create());
        mat4.rotateY(matrix, matrix, -normalizedX * halfFov * aspect);
        mat4.rotateX(matrix, matrix, normalizedY * halfFov);
        matrix[12] = normalizedX * Math.tan(halfFov) * near * aspect;
        matrix[13] = normalizedY * Math.tan(halfFov) * near;
        matrix[14] = -near;
        mat4.multiply(matrix, viewMatrixInverse, matrix);

        const gamepad = this._gamepads[i];
        const gamepadPose = gamepad.pose;
        // HAX - we are forcing a 'local' type experience, where there is no floor-level. This can cause issue for other types

        mat4.copy(this._floorOffsetMatrix, matrix);
        /*
        // get session type. if local-floor offset by stage height
        if (this._frameSession.enabledFeatures.has("local-floor")) {
          this._floorOffsetMatrix.y -= this._stageMatrix[13]; // subtract the stage height if local-floor
        }
        */

        mat4.getTranslation(this._floorOffsetMatrix, matrix);
        mat4.getRotation(gamepadPose.orientation, matrix);

        const pose = inputSourceImpl.getXRPose(coordinateSystem, poseType);

        mat4.fromRotationTranslation(
          pose.transform.matrix,
          gamepadPose.orientation,
          this._floorOffsetMatrix
        );
        mat4.invert(pose.transform.inverse.matrix, pose.transform.matrix);
        return pose;
      }
    }
    return null;
  }

  onWindowResize() {
    this._sessions.forEach((value, key) => {
      // var layer = value.baseLayer
      // layer.width = layer.context.canvas.width = this._wrapperDiv.clientWidth * window.devicePixelRatio;
      // layer.height = layer.context.canvas.height = this._wrapperDiv.clientHeight * window.devicePixelRatio;
    });
  }
}

const createGamepad = (id, hand, buttonNum, hasPosition) => {
  const buttons = [];
  for (let i = 0; i < buttonNum; i++) {
    buttons.push({
      pressed: false,
      touched: false,
      value: 0.0,
    });
  }
  return {
    id: id || "",
    pose: {
      hasPosition: hasPosition,
      position: new Float32Array([0, 0, 0]),
      orientation: new Float32Array([0, 0, 0, 1]),
    },
    buttons: buttons,
    hand: "",
    mapping: "xr-standard",
    axes: [0, 0],
    index: -1,
    connected: true,
    profiles: ["generic-touchscreen"],
  };
};

let SESSION_ID = 100;
class Session {
  constructor(mode, enabledFeatures) {
    this.mode = mode;
    this.enabledFeatures = enabledFeatures;
    this.immersive = mode == "immersive-ar";
    this.ended = null; // boolean
    this.baseLayer = null; // XRWebGLLayer
    this.id = ++SESSION_ID;
  }
}

class ARWatcher extends ARKitWatcher {
  constructor(arKitWrapper, arKitDevice) {
    super(arKitWrapper);
    this._arKitDevice = arKitDevice;
  }

  handleARKitInit() {
    window.VLaunch.log("ARKit initialized");
    let event = new CustomEvent("vlaunch-ar-status", {
      detail: { status: "started" },
    });
    dispatchEvent(event);
  }

  handleArTrackingChanged(e) {
    window.VLaunch.log("Tracking changed " + JSON.stringify(e.detail));
    let event = new CustomEvent("vlaunch-ar-tracking", { detail: e.detail });
    dispatchEvent(event);
  }

  handleARKitUpdate(event) {
    this._arKitDevice.setBaseViewMatrix(this._arKitWrapper._cameraTransform);
    this._arKitDevice.setProjectionMatrix(this._arKitWrapper._projectionMatrix);
  }

  handleOnError(...args) {
    console.error("ARKit error", ...args);
  }

  handleUserStoppedAR() {
    this._arKitDevice.userEndedSession();
    window.VLaunch.log("ARKit stopped");
    let event = new CustomEvent("vlaunch-ar-status", {
      detail: { status: "stopped" },
    });
    dispatchEvent(event);
  }
}
